import { components, components as pageMnt } from '@doltech/pageManagement/dto';
import { useFromScreens } from '@doltech/ui/lib/hooks/useDeviceQuery.hook';
import { SwiperContent } from '@doltech/ui/lib/shared/components/PageSectionStructure/Content/SwiperContent';
import { PageSectionStructureProps } from '@doltech/ui/lib/shared/components/PageSectionStructure/PageSectionStructure';
import { PageSectionStructureV2 } from '@doltech/ui/lib/shared/components/PageSectionStructure/PageSectionStructureV2';
import { BookPageSectionEnum } from '@doltech/utils/lib/constants';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { BookOverviewCard } from '../../../../home-page/ie-online-test/BookOverviewCard';

const GridOneColumn = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
`;

export const BookSeriesViewModeEnum = {
  TWO_BOOKS: 'two-books',
  THREE_BOOKS: 'three-books',
  FOUR_TO_SIX_BOOKS: 'four-to-six-books',
  MORE_THAN_SIX_BOOKS: 'more-than-six-books',
  MORE_THAN_SIX_BOOKS_WITHOUT_MOBILE_SWIPER: 'more-than-six-books-without-mobile-swiper',
} as const;

export type BookSeriesViewModeEnum =
  (typeof BookSeriesViewModeEnum)[keyof typeof BookSeriesViewModeEnum];

interface LandingBookSeriesProps extends PageSectionStructureProps {
  header?: string;
  name?: string;
  data: pageMnt['schemas']['BookDetailDTO'][];
  vMode?: BookSeriesViewModeEnum;
  isHorizontalCard?: boolean;
  withCardDescription?: boolean;
  pageSections?: components['schemas']['PageSectionDTO'][];
  sectionType?: BookPageSectionEnum;
  navTop?: number;
}

export const getBookSeriesViewMode = (bookLength: number): BookSeriesViewModeEnum => {
  if (bookLength <= 2) return BookSeriesViewModeEnum.TWO_BOOKS;
  if (bookLength === 3) return BookSeriesViewModeEnum.THREE_BOOKS;
  if (bookLength <= 6) return BookSeriesViewModeEnum.FOUR_TO_SIX_BOOKS;
  return BookSeriesViewModeEnum.MORE_THAN_SIX_BOOKS;
};

export const BookSeriesCard = ({ book, viewMode, isHorizontalCard, withCardDescription }) => {
  const [, from1024] = useFromScreens([0, 1024]);

  let imageWidth = null;
  let isHorizontalDisplay = isHorizontalCard;
  if (viewMode === BookSeriesViewModeEnum.TWO_BOOKS) {
    if (from1024) {
      imageWidth = 252;
    } else {
      imageWidth = 128;
    }
  } else if (
    viewMode === BookSeriesViewModeEnum.FOUR_TO_SIX_BOOKS ||
    viewMode === BookSeriesViewModeEnum.MORE_THAN_SIX_BOOKS_WITHOUT_MOBILE_SWIPER
  ) {
    imageWidth = 128;
    isHorizontalDisplay = true;
  }

  return (
    <BookOverviewCard
      data={book}
      key={book.id}
      isHorizontalDisplay={isHorizontalDisplay}
      imageWidth={imageWidth}
      withDescription={withCardDescription}
    />
  );
};

/**
 * Currently this book series list will have 4 different UI based on the book count
 * - Two books: 2 column
 * - Three books: 3 column
 * - Four to Six books: 2-3 column adaptive
 * - More than Six books: 4 column with swiper navigation
 *
 */
export const LandingBookSeries = observer((props: LandingBookSeriesProps) => {
  const {
    header,
    data,
    name,
    vMode,
    isHorizontalCard = false,
    withCardDescription = false,
    slideWidths = [252, 252, 252],
    breakpoints = [0, 768, 769],
    pageSections = [],
    sectionType,
    gridColumns,
    contentStyle,
    navTop = 168,
  } = props;

  const viewMode = vMode || getBookSeriesViewMode(data.length);

  const pageSection = React.useMemo(
    () => pageSections.find((item) => item.type === sectionType),
    [pageSections, sectionType]
  );
  return (
    <PageSectionStructureV2
      oldStyling
      title={header || `Bài test từ ${name}`}
      pageSection={pageSection}
      content={
        <GridOneColumn>
          <SwiperContent
            data={data}
            renderCard={(book) => (
              <BookSeriesCard
                book={book}
                viewMode={viewMode}
                isHorizontalCard={isHorizontalCard}
                withCardDescription={withCardDescription}
              />
            )}
            slideWidths={slideWidths}
            breakpoints={breakpoints}
            gridColumns={gridColumns}
            customNavigation={
              contentStyle === 'slide'
                ? [
                    {
                      breakpoint: 1279,
                      visible: false,
                      top: '0%',
                    },
                    {
                      breakpoint: 1280,
                      visible: true,
                      top: `${navTop / 2}px`,
                    },
                  ]
                : undefined
            }
          />
        </GridOneColumn>
      }
    />
  );
});
